import React, { FC } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { cn } from 'components/lib/utils';

import Button from '../Button/Button';
import { RadioButtonOptionsProps } from '../RadioButton/RadioButton';
import RadioButtonGroup from '../RadioButton/RadioButtonGroup';
import Typography from '../Typography/Typography';

type FormHeaderProps = {
  dataTestId?: string;
  mainButtonText?: string;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  onCloseForm: () => void;
  onMainButtonClick?: () => void;
  closeFormOnSubmit?: boolean;
  onOptionChange?: (value: string) => void;
  selectedOption?: string;
  title?: string;
  TitleComponent?: React.FC;
  SecondarySection?: React.FC;
  options?: RadioButtonOptionsProps[];
  isFormValid?: boolean;
  closeIcon?: string;
  showPreviewButton?: boolean;
  onPreviewForm?: () => void;
  className?: string;
};

const FormHeader: FC<FormHeaderProps> = ({
  dataTestId,
  mainButtonText,
  onCloseForm,
  onMainButtonClick,
  closeFormOnSubmit = true,
  onOptionChange,
  selectedOption,
  isFormValid = true,
  closeIcon = 'fa-xmark',
  title = '',
  TitleComponent,
  options = [],
  secondaryButtonText,
  SecondarySection,
  onSecondaryButtonClick,
  className
}) => {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-8 py-5 border-b border-base-sidebarBorder bg-white sticky top-0 z-20',
        className
      )}
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-4 w-[20%]">
        <FontAwesomeIcon
          icon={`fa-regular ${closeIcon}`}
          className="text-neutral-700 cursor-pointer"
          onClick={onCloseForm}
          data-testid={`${dataTestId}-CloseButton`}
        />
        {title && (
          <Typography
            className="font-medium text-base-mutedForeground border-l border-base-border h-6 pl-4 truncate"
            data-testid={`${dataTestId}-Title`}
          >
            {title}
          </Typography>
        )}
        {TitleComponent && <TitleComponent />}
      </div>
      <div className="flex items-center gap-[19px]">
        {options?.length > 0 && (
          <RadioButtonGroup
            dataTestId={`${dataTestId}-State`}
            value={selectedOption}
            onValueChange={onOptionChange}
            className="border-r border-base-border h-6 pr-7"
            options={options}
          />
        )}
        {SecondarySection && <SecondarySection />}
        <div className="flex gap-2.5">
          {secondaryButtonText && onSecondaryButtonClick && (
            <Button
              onClick={() => onSecondaryButtonClick()}
              variant="outline"
              type="button"
            >
              {secondaryButtonText}
            </Button>
          )}
          {mainButtonText && onMainButtonClick && (
            <Button
              onClick={() => {
                onMainButtonClick();
                if (closeFormOnSubmit) {
                  onCloseForm();
                }
              }}
              className="bg-indigo-700 hover:bg-button-hover-create"
              type="button"
              disabled={!isFormValid}
            >
              {mainButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormHeader;
